import { CheckIcon } from "@heroicons/react/20/solid";
import {
  useMeeting,
  useParticipant,
  usePubSub,
} from "@videosdk.live/react-sdk";
import { useEffect, useMemo, useRef } from "react";
import { toast, Bounce } from "react-toastify";
import { useAppContext } from "../AppContextDef";
import { useTranslation } from "react-i18next";
import { APP_FONT_STACK } from "../utils/common";
import usePubsubFunction from "../utils/usePubsubFunction";
import useFaceVerification from "../hooks/useFaceVerification";
import useIdentityLog from "../hooks/useIdentityLog";
import { EXECUTED_BY } from "../utils/identityLog";
import RecorderStatsPanel from "./RecorderStatsPanel";

const RemoteParticipantView = ({ showStats = true }) => {
  const micRef = useRef(null);
  const videoRef = useRef(null);

  const {
    selectedLang,
    faceVerified,
    agreedData,
    faceDetectError,
    currentPageData,
    isAbortSignal,
    allPubsubData,
    delegatedApiSuccess,
    isMeetingEnded,
    recorderRefImageRef,
    setFaceVerified,
    setAgreedData,
    setFaceDetectError,
    setLivenessResult,
    setFaceMatchScore,
    setFaceMatchVerdict,
  } = useAppContext();

  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    changeLanguage(selectedLang?.lang);
  }, [selectedLang]);

  const { participants } = useMeeting({});
  const nonLocalParticipantId = Array.from(participants.values()).find(
    (p) => !p.local,
  )?.id;

  const { webcamStream, micStream, webcamOn, micOn, captureImage } =
    useParticipant(nonLocalParticipantId);

  // ---- recorder-owned verification engine --------------------------------
  const pubsubPublishMethod = usePubsubFunction();
  const { publish: faceDetectErrorPublish } = usePubSub(`FACE_DETECT_ERROR`);
  const { publish: faceVerifiedPublish } = usePubSub(`FACE_VERIFIED`);
  const { publish: agreedPublish } = usePubSub(`AGREED`);
  const { publish: abortSignalPublish } = usePubSub(`ABORT_SIGNAL`);
  const { publish: delegateApiPublish } = usePubSub(`DELEGATE_API`);
  const { publish: verificationOutcomePublish } = usePubSub(
    `VERIFICATION_OUTCOME`,
  );

  const abortControllerRef = useRef(new AbortController());
  const isRequestInProgress = useRef(false);

  // Sole writer of the IDENTITY call log — for this peer's own calls here, and
  // for delegated ones via the IDENTITY_RESULT subscription in PubSubHandler.
  const logIdentityCall = useIdentityLog();

  const faceVerification = useFaceVerification({
    mode: "owner",
    captureImage,
    webcamOn,
    token: allPubsubData?.videoSDKToken,
    roomId: allPubsubData?.videoSDKRoomId,
    abortSignalRef: abortControllerRef,
    currentPage: currentPageData,
    referenceImageRef: recorderRefImageRef,
    publishers: {
      pubsubPublishMethod,
      faceDetectErrorPublish,
      faceVerifiedPublish,
      agreedPublish,
      abortSignalPublish,
      delegateApiPublish,
      verificationOutcomePublish,
    },
    callbacks: {
      onAgreed: (isAgreed) => {
        console.log(`[Recorder] verdict AGREED -> ${isAgreed}`);
        setAgreedData(isAgreed);
      },
      onVerified: (isVerified) => {
        console.log(`[Recorder] verdict FACE_VERIFIED -> ${isVerified}`);
        setFaceVerified({ isVerified, timestamp: Date.now() });
      },
      onError: (err) => {
        console.log(`[Recorder] verdict ERROR -> ${err}`);
        setFaceDetectError({ err, timestamp: Date.now() });
      },
      // Both fire only when their endpoint actually answered, so a dropped
      // request leaves the last known score on the panel.
      // One IDENTITY log row per answered call this peer made. Delegated calls
      // do NOT arrive here — they fire on the client and come back over
      // IDENTITY_RESULT, so they are logged from PubSubHandler instead.
      onApiResult: ({ apiName, body, page }) =>
        logIdentityCall({
          apiName,
          body,
          page,
          executedBy: EXECUTED_BY.RECORDER,
        }),
      onLivenessResult: (result) => setLivenessResult(result),
      onFaceMatchResult: (result) => {
        setFaceMatchScore(result?.score ?? null);
        setFaceMatchVerdict(
          result ? { verified: result.verified, timestamp: Date.now() } : null,
        );
      },
      onFailure: (apiName, count) =>
        console.log(
          `[Recorder] "${apiName}" failed (consecutive failures: ${count}/3)`,
        ),
      onDelegate: (apiName) =>
        console.warn(
          `[Recorder] 3 failures on "${apiName}" — DELEGATING to client (pausing this API on recorder)`,
        ),
      onOutcome: (apiName, outcome) =>
        console.log(`[Recorder] publishing verification outcome ${apiName} -> ${outcome}`),
    },
  });

  // Keep the latest pipeline closure so the interval always runs fresh state.
  const pipelineRef = useRef(faceVerification.runOwnerPipeline);
  useEffect(() => {
    pipelineRef.current = faceVerification.runOwnerPipeline;
  });

  // 5s verification loop, running off the customer's already-streamed frames.
  useEffect(() => {
    const ready =
      webcamOn &&
      webcamStream &&
      nonLocalParticipantId &&
      !isMeetingEnded &&
      allPubsubData?.videoSDKToken;

    console.log("[Recorder] verification loop gate", {
      page: currentPageData,
      webcamOn,
      hasStream: !!webcamStream,
      nonLocalParticipantId,
      isMeetingEnded,
      hasToken: !!allPubsubData?.videoSDKToken,
      ready: !!ready,
    });

    if (ready) {
      const makeRequest = () => {
        if (!isRequestInProgress.current) {
          isRequestInProgress.current = true;
          console.log(
            `[Recorder] running verification tick (page: ${currentPageData})`,
          );
          pipelineRef
            .current()
            .catch((err) =>
              console.error("[Recorder] verification tick failed", err),
            )
            .finally(() => (isRequestInProgress.current = false));
        } else {
          console.log("[Recorder] skipping tick — previous still in progress");
        }
      };

      if (currentPageData !== "PersonalDetails") {
        makeRequest();
      }

      console.log("[Recorder] verification loop STARTED (5s interval)");
      const intervalId = setInterval(makeRequest, 5000);
      return () => {
        console.log("[Recorder] verification loop STOPPED");
        clearInterval(intervalId);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    webcamOn,
    webcamStream,
    nonLocalParticipantId,
    isMeetingEnded,
    allPubsubData?.videoSDKToken,
    currentPageData,
  ]);

  // Handback: the client reported a successful fallback run for an API ->
  // mark that check done and resume the pipeline on the next tick.
  //
  // The reported page is passed through and re-checked inside handback, which
  // drops a success that belongs to an earlier step. The client applies the
  // mirror-image guard before it runs a delegated API (see ParticipantView);
  // this is the other half, and it is what stops one delegated spoof pass from
  // satisfying the liveness check for every page that follows.
  useEffect(() => {
    if (delegatedApiSuccess?.apiName) {
      console.log(
        `[Recorder] client reported SUCCESS for delegated "${delegatedApiSuccess.apiName}" — handing back, recorder will resume this API`,
        delegatedApiSuccess,
      );
      faceVerification.handback(
        delegatedApiSuccess.apiName,
        delegatedApiSuccess.currentPage,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delegatedApiSuccess, currentPageData]);

  // Cancel any in-flight owner requests when this view unmounts (page change /
  // meeting end). A fresh mount gets a fresh controller + counters.
  useEffect(() => {
    const controller = abortControllerRef.current;
    return () => {
      console.log("[Recorder] view unmounting — aborting in-flight requests");
      controller.abort();
    };
  }, []);

  const videoStream = useMemo(() => {
    if (webcamOn && webcamStream) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(webcamStream.track);
      return mediaStream;
    }
    return null;
  }, [webcamStream, webcamOn]);

  useEffect(() => {
    if (micRef.current) {
      if (micOn && micStream) {
        try {
          const mediaStream = new MediaStream();
          mediaStream.addTrack(micStream.track);
          micRef.current.srcObject = mediaStream;
          micRef.current
            .play()
            .catch((error) =>
              console.error("micRef.current.play() failed", error),
            );
        } catch (error) {
          console.error("Error handling mic stream:", error);
          micRef.current.srcObject = null;
        }
      } else {
        micRef.current.srcObject = null;
      }
    }
  }, [micStream, micOn]);

  useEffect(() => {
    if (videoRef.current) {
      if (webcamOn && videoStream) {
        try {
          videoRef.current.srcObject = videoStream;
          videoRef.current.setAttribute("playsinline", "");
          videoRef.current.setAttribute("x5-playsinline", "");
          videoRef.current.setAttribute("webkit-playsinline", "");
          videoRef.current
            .play()
            .catch((error) =>
              console.error("videoRef.current.play() failed", error),
            );
        } catch (error) {
          console.error("Error handling webcam stream:", error);
          videoRef.current.srcObject = null;
        }
      } else {
        videoRef.current.srcObject = null;
      }
    }
  }, [videoStream, webcamOn]);

  const fontFamilyCSS = APP_FONT_STACK;

  useEffect(() => {
    if (faceDetectError !== null) {
      let message =
        faceDetectError?.err === "NO_FACE_DETECTED"
          ? "No face detected. Please ensure your face is clearly visible."
          : faceDetectError?.err === "FACE_MATCH_UNSUCESSFUL"
            ? "Face Match Unsucessful!"
            : faceDetectError?.err === "MULTIPLE_FACES_DETECTED"
              ? "Multiple faces detected. Please make sure only one person is in view!"
              : faceDetectError?.err === "SPOOF_DETECTED"
                ? "Make sure a real person is in view and looking towards camera in a well-lit area!"
                : "";

      if (!isAbortSignal) {
        new Audio(`https://cdn.videosdk.live/icici/error_1.wav`).play();
        toast.error(`${t(message)}`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
          style: { fontFamily: fontFamilyCSS },
        });
      }
    }
  }, [faceDetectError, isAbortSignal]);

  const isVerified = faceVerified?.isVerified;

  const videoBlock = (
    <>
      <div className="relative aspect-video w-full max-w-[680px] flex-none">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={true}
          className={`h-full w-full ${
            isVerified
              ? agreedData
                ? "border-2 border-green-600"
                : "border-2 border-red-700"
              : ""
          }`}
          style={{ borderRadius: 16 }}
        />

        {!isVerified && (
          <>
            <div className="absolute inset-0 z-30 rounded-xl bg-[#16161680]" />
            <div className="absolute inset-0 z-40 flex items-center justify-center p-4">
              <p
                style={{ fontFamily: fontFamilyCSS }}
                className="mb-0 text-center text-xl text-white"
              >
                {`${t("Please hold on while we verify your video")}...`}
              </p>
            </div>
          </>
        )}

        {isVerified && agreedData && (
          <div className="absolute bottom-3 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-lg bg-[#000000B3] px-3 py-1.5 backdrop-blur-sm">
            <CheckIcon className="h-4 w-4 text-green-400 lg:h-5 lg:w-5" />
            <p
              style={{ fontFamily: fontFamilyCSS }}
              className="mb-0 whitespace-nowrap text-sm text-white lg:text-base"
            >
              {t("Face detected")}
            </p>
          </div>
        )}
      </div>

      <audio ref={micRef} autoPlay playsInline muted={false} />
    </>
  );

  // Holds the video's footprint while the customer's webcam is momentarily off
  // (mid camera-switch / brief drop) so the stats panel below doesn't jump.
  const videoPlaceholder = (
    <div className="relative flex aspect-video w-full max-w-[680px] flex-none items-center justify-center rounded-2xl bg-[#161616]">
      <div
        style={{ borderTopColor: "#EA7617" }}
        className="h-10 w-10 animate-spin rounded-full border-4 border-gray-100 ease-linear"
      />
    </div>
  );

  // VideoConsent (showStats=false): the panel lives in a sibling column
  // (VideoConsentSection), so here we only render the video, and nothing while
  // the webcam is off — unchanged behavior.
  if (!showStats) {
    return webcamOn ? (
      <div className="flex w-full flex-col items-center gap-3">{videoBlock}</div>
    ) : null;
  }

  // Other three pages: the panel renders inside this column, which is therefore
  // its own scroll region (its only overflow ancestor is Index.js's
  // overflow-y-hidden). Keep the panel mounted even while the customer's webcam
  // is momentarily off — previously the whole view (video AND stats) returned
  // null on webcam-off, so switching cameras made the stats panel vanish and
  // remount, dropping its polled values for ~1s. Now only the video area toggles.
  return (
    <div className="flex md:flex-1 min-h-0 w-full flex-col items-center gap-4 overflow-y-auto scrollable-content scrollbar scrollbar-primary">
      {webcamOn ? videoBlock : videoPlaceholder}

      <div className="w-full flex-none">
        <RecorderStatsPanel />
      </div>
    </div>
  );
};

export default RemoteParticipantView;
